/**
 * useAiImprove hook
 *
 * Manages the improve-with-AI conversation for a selected code snippet.
 * The user provides an instruction first; the hook fires the initial improve
 * request, then supports unlimited follow-up messages in the same thread.
 */

import { useState, useCallback, useRef, useMemo } from 'react';
import {
  AiImproveState,
  AiChatMessage,
  AiTokenUsage,
  OpenRouterModel,
} from '@/types/ai';
import {
  createAiClient,
  streamChatCompletion,
  ChatMessage,
  calculateCost,
  aggregateTokenUsage,
} from '@/services/ai';
import {
  buildImproveSystemPrompt,
  buildImproveUserMessage,
} from '@/services/aiPrompts';

const INITIAL_STATE: AiImproveState = {
  step: 'idle',
  selectedCode: '',
  contextCode: '',
  userInstruction: '',
  messages: [],
  currentContent: '',
};

interface UseAiImproveReturn {
  state: AiImproveState;
  isImproving: boolean;
  tokenUsage: AiTokenUsage;
  improve: (
    apiKey: string,
    selectedCode: string,
    contextCode: string,
    instruction: string,
    model: string,
    modelInfo: OpenRouterModel | null,
    systemPromptOverride?: string,
  ) => Promise<void>;
  sendFollowUp: (
    apiKey: string,
    question: string,
    model: string,
    modelInfo: OpenRouterModel | null,
  ) => Promise<void>;
  cancel: () => void;
  reset: () => void;
}

export function useAiImprove(): UseAiImproveReturn {
  const [state, setState] = useState<AiImproveState>(INITIAL_STATE);
  const [modelInfo, setModelInfo] = useState<OpenRouterModel | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  // Stores the system prompt used for the current conversation so follow-ups
  // can use the same prompt (including any override supplied at improve() time).
  const systemPromptRef = useRef<string>('');

  // Track token usage across all turns
  const [tokenTurns, setTokenTurns] = useState<
    Array<{ prompt: number; completion: number }>
  >([]);

  const isImproving = state.step === 'improving';

  const tokenUsage = useMemo((): AiTokenUsage => {
    const aggregated = aggregateTokenUsage(tokenTurns);
    let estimatedCost = 0;
    if (modelInfo) {
      estimatedCost = calculateCost(
        modelInfo,
        aggregated.promptTokens,
        aggregated.completionTokens,
      );
    }
    return { ...aggregated, estimatedCost };
  }, [tokenTurns, modelInfo]);

  /**
   * Run the AI for a given set of messages (shared by improve + follow-up).
   */
  const runStream = useCallback(
    async (
      apiKey: string,
      model: string,
      messages: ChatMessage[],
    ): Promise<string> => {
      abortControllerRef.current = new AbortController();
      const client = createAiClient(apiKey);

      let fullContent = '';

      await streamChatCompletion(
        client,
        model,
        messages,
        {
          onChunk: (chunk) => {
            fullContent += chunk;
            setState((prev) => ({ ...prev, currentContent: fullContent }));
          },
          onComplete: (_content, usage) => {
            setTokenTurns((prev) => [
              ...prev,
              { prompt: usage.prompt, completion: usage.completion },
            ]);
          },
          onError: (err) => {
            if (err.message !== 'Generation cancelled') {
              setState((prev) => ({
                ...prev,
                step: 'error',
                error: err.message,
                currentContent: '',
              }));
            }
          },
        },
        abortControllerRef.current.signal,
      );

      return fullContent;
    },
    [],
  );

  /**
   * Kick off the initial improvement for a selected code snippet.
   * Stores selectedCode + contextCode + instruction in state.
   */
  const improve = useCallback(
    async (
      apiKey: string,
      selectedCode: string,
      contextCode: string,
      instruction: string,
      model: string,
      newModelInfo: OpenRouterModel | null,
      systemPromptOverride?: string,
    ) => {
      setModelInfo(newModelInfo);
      setTokenTurns([]);

      const firstUserMessage: AiChatMessage = {
        role: 'user',
        content: buildImproveUserMessage(selectedCode, instruction),
      };

      setState({
        step: 'improving',
        selectedCode,
        contextCode,
        userInstruction: instruction,
        messages: [firstUserMessage],
        currentContent: '',
      });

      const systemPrompt =
        systemPromptOverride ?? buildImproveSystemPrompt(contextCode);
      // Store for follow-up turns
      systemPromptRef.current = systemPrompt;

      const apiMessages: ChatMessage[] = [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: firstUserMessage.content },
      ];

      try {
        const assistantContent = await runStream(apiKey, model, apiMessages);

        if (assistantContent) {
          setState((prev) => ({
            ...prev,
            step: 'complete',
            messages: [
              ...prev.messages,
              { role: 'assistant', content: assistantContent },
            ],
            currentContent: '',
          }));
        }
      } catch (err) {
        if (err instanceof Error && err.message === 'Generation cancelled') {
          setState((prev) => ({
            ...prev,
            step: 'idle',
            currentContent: '',
          }));
        }
      }
    },
    [runStream],
  );

  /**
   * Send a follow-up question in the same conversation thread.
   */
  const sendFollowUp = useCallback(
    async (
      apiKey: string,
      question: string,
      model: string,
      newModelInfo: OpenRouterModel | null,
    ) => {
      if (!question.trim()) return;

      setModelInfo(newModelInfo);

      const userMessage: AiChatMessage = { role: 'user', content: question };

      // Optimistically add the user message to the thread
      let updatedMessages: AiChatMessage[] = [];
      setState((prev) => {
        updatedMessages = [...prev.messages, userMessage];
        return {
          ...prev,
          step: 'improving',
          messages: updatedMessages,
          currentContent: '',
        };
      });

      // Reuse the system prompt from the initial improve() call (may be an override)
      const systemPrompt =
        systemPromptRef.current || buildImproveSystemPrompt(state.contextCode);
      const apiMessages: ChatMessage[] = [
        { role: 'system', content: systemPrompt },
        ...updatedMessages.map((m) => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
        })),
      ];

      try {
        const assistantContent = await runStream(apiKey, model, apiMessages);

        if (assistantContent) {
          setState((prev) => ({
            ...prev,
            step: 'complete',
            messages: [
              ...prev.messages,
              { role: 'assistant', content: assistantContent },
            ],
            currentContent: '',
          }));
        }
      } catch (err) {
        if (err instanceof Error && err.message === 'Generation cancelled') {
          setState((prev) => ({
            ...prev,
            step: 'complete',
            currentContent: '',
          }));
        }
      }
    },
    [runStream, state.contextCode],
  );

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    abortControllerRef.current?.abort();
    setState(INITIAL_STATE);
    setTokenTurns([]);
    setModelInfo(null);
    systemPromptRef.current = '';
  }, []);

  return {
    state,
    isImproving,
    tokenUsage,
    improve,
    sendFollowUp,
    cancel,
    reset,
  };
}
